import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { EvaluationListItem } from "../../components/evaluations/EvaluationListItem";
import { EvaluationDetailView } from "../../components/evaluations/EvaluationDetailView";
import { SessionFilter, type FilterSuggestion } from "../../components/SessionFilter";
import { Button, Icon, Spinner, Text, View } from "../../components/base";
import { EmptyState } from "../../components/base/EmptyState";
import { useSearchParam } from "../../hooks/useSearchParam";
import { useAppDispatch, useAppSelector } from "../../redux";
import { agentEvaluationActions, Evaluation } from "../../redux/reducers/evaluationSlices";
import { environmentActions } from "../../redux/reducers/environmentSlice";
import { evaluatorActions } from "../../redux/reducers/evaluatorSlice";
import { objectsEqual } from "../../utils/parse";

const EVALUATION_FILTER_SUGGESTIONS: FilterSuggestion[] = [
  {
    field: "status",
    label: "status",
    description: "Evaluation status",
    examples: ["status = complete", "status IN (pending, error, skipped)"],
    operators: ["equals", "in"],
  },
  {
    field: "evalType",
    label: "evalType",
    description: "Evaluator type",
    examples: ["evalType = judge", "evalType IN (judge, extraction, deterministic)"],
    operators: ["equals", "in"],
  },
  {
    field: "trigger",
    label: "trigger",
    description: "Evaluation trigger",
    examples: ["trigger = automatic"],
    operators: ["equals", "in"],
  },
  {
    field: "success",
    label: "success",
    description: "Success outcome (judge + deterministic)",
    examples: ["success = true", "success = false"],
    operators: ["equals"],
  },
  {
    field: "evaluatorId",
    label: "evaluatorId",
    description: "Evaluator ID",
    examples: ["evaluatorId = eva_123"],
    operators: ["equals", "in"],
  },
  {
    field: "createdAt",
    label: "createdAt",
    description: "Evaluation creation timestamp",
    examples: ["createdAt >= 2024-01-01"],
    operators: ["equals", "gt", "lt", "gte", "lte"],
  },
  {
    field: "updatedAt",
    label: "updatedAt",
    description: "Evaluation updated timestamp",
    examples: ["updatedAt >= 2024-01-01"],
    operators: ["equals", "gt", "lt", "gte", "lte"],
  },
  {
    field: "session.status",
    label: "session.status",
    description: "Session status",
    examples: ["session.status = completed"],
    operators: ["equals", "in"],
  },
  {
    field: "session.origin",
    label: "session.origin",
    description: "Session origin",
    examples: ["session.origin = phone"],
    operators: ["equals", "in"],
  },
  {
    field: "session.agentEnvironmentId",
    label: "session.environment",
    description: "Session environment (matches by name or ID)",
    examples: ["session.environment = prod"],
    operators: ["equals", "in"],
  },
  {
    field: "session.createdAt",
    label: "session.createdAt",
    description: "Session creation timestamp",
    examples: ["session.createdAt >= 2024-01-01"],
    operators: ["equals", "gt", "lt", "gte", "lte"],
  },
];

const EVALUATION_PAGE_LIMIT = "50";

export const AgentEvaluationEvaluationsTab = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const evaluationIdParam = useSearchParam("evaluation");
  const [filters, setFilters] = useState<Record<string, Record<string, unknown>>>({});
  const [page, setPage] = useState(1);

  const evaluationState = useAppSelector((state) => state.agentEvaluation);
  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);
  const agentEnvironmentEntities = useAppSelector((state) => state.agentEnvironment.entities);
  const environmentEntities = useAppSelector((state) => state.environment.entities);

  const agentEnvironments = useMemo(() => {
    return Object.values(agentEnvironmentEntities).filter((environment) => !agentId || environment.agentId === agentId);
  }, [agentEnvironmentEntities, agentId]);

  const resolveEnvironmentId = (value: unknown) => {
    if (typeof value !== "string") {
      return value;
    }

    const matchingEnvironment = agentEnvironments.find((environment) => environment.id === value || environment.name === value);

    return matchingEnvironment ? matchingEnvironment.id : value;
  };

  const evaluations = useMemo(() => {
    return evaluationState.ids.map((id) => evaluationState.entities[id]).filter((evaluation): evaluation is Evaluation => Boolean(evaluation));
  }, [evaluationState.entities, evaluationState.ids]);

  const selectedEvaluation = useMemo(() => {
    if (!evaluationIdParam) {
      return null;
    }

    return evaluations.find((evaluation) => evaluation.id === evaluationIdParam) || null;
  }, [evaluations, evaluationIdParam]);

  useEffect(() => {
    dispatch(evaluatorActions.get({}));
    dispatch(environmentActions.get({}));
  }, [dispatch]);

  useEffect(() => {
    if (!agentId) {
      return;
    }

    dispatch(
      agentEvaluationActions.get({
        agentId,
        page: page.toString(),
        limit: EVALUATION_PAGE_LIMIT,
        ...filters,
      }),
    );
  }, [agentId, dispatch, filters, page]);

  useEffect(() => {
    if (!agentId) {
      return;
    }

    if (evaluationIdParam && !selectedEvaluation) {
      navigate(`/agents/${agentId}/evaluation/evaluations`, { replace: true });
    }
  }, [agentId, evaluationIdParam, navigate, selectedEvaluation]);

  const handleFilterChange = (incomingFilters: Record<string, Record<string, unknown>>) => {
    const normalizedFilters = Object.entries(incomingFilters).reduce(
      (acc, [key, value]) => {
        acc[key] = { ...(value || {}) };
        return acc;
      },
      {} as Record<string, Record<string, unknown>>,
    );

    const normalizeDateFilter = (filterKey: string) => {
      if (!normalizedFilters[filterKey]) return;
      Object.entries(normalizedFilters[filterKey]).forEach(([operator, value]) => {
        if (!value) return;
        const toIso = (input: unknown) => {
          try {
            return new Date(input as string).toISOString();
          } catch {
            return input;
          }
        };

        if (Array.isArray(value)) {
          normalizedFilters[filterKey][operator] = value.map((entry) => toIso(entry));
        } else {
          normalizedFilters[filterKey][operator] = toIso(value);
        }
      });
    };

    ["createdAt", "updatedAt", "session.createdAt"].forEach(normalizeDateFilter);

    if (normalizedFilters["session.agentEnvironmentId"]) {
      Object.entries(normalizedFilters["session.agentEnvironmentId"]).forEach(([operator, value]) => {
        if (Array.isArray(value)) {
          normalizedFilters["session.agentEnvironmentId"][operator] = value.map((entry) => resolveEnvironmentId(entry));
        } else {
          normalizedFilters["session.agentEnvironmentId"][operator] = resolveEnvironmentId(value);
        }
      });
    }

    if (normalizedFilters.success) {
      Object.entries(normalizedFilters.success).forEach(([operator, value]) => {
        const convertBoolean = (input: unknown) => {
          if (typeof input === "string") {
            const lower = input.toLowerCase();
            if (lower === "true" || lower === "false") {
              return lower === "true";
            }
          }
          return input;
        };

        if (Array.isArray(value)) {
          normalizedFilters.success[operator] = value.map((entry) => convertBoolean(entry));
        } else {
          normalizedFilters.success[operator] = convertBoolean(value);
        }
      });
    }

    if (objectsEqual(filters, normalizedFilters)) {
      return;
    }

    setFilters(normalizedFilters);
    setPage(1);
  };

  const handleSelectEvaluation = (evaluationId: string) => {
    if (!agentId) {
      return;
    }
    navigate(`/agents/${agentId}/evaluation/evaluations?evaluation=${evaluationId}`);
  };

  const handleClearSelection = () => {
    if (!agentId) {
      return;
    }
    navigate(`/agents/${agentId}/evaluation/evaluations`);
  };

  const metadataLimit = evaluationState.metadata.limit || Number(EVALUATION_PAGE_LIMIT);
  const totalPages = Math.max(1, Math.ceil((evaluationState.metadata.total || 0) / metadataLimit));
  const isLoading = evaluationState.isLoading;

  const selectedEvaluator = selectedEvaluation ? evaluatorEntities[selectedEvaluation.evaluatorId] || null : null;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <View className="relative flex flex-col h-full min-h-0 overflow-hidden">
      <View className="flex flex-col gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <SessionFilter
          onChange={handleFilterChange}
          label="Filter Evaluations"
          placeholder="Try: status = complete AND session.environment = prod"
          filterSuggestions={EVALUATION_FILTER_SUGGESTIONS}
        />
      </View>

      <View className="flex flex-row flex-1 overflow-hidden">
        <View className={clsx("flex flex-col flex-1 p-4 transition-all duration-300 overflow-hidden", selectedEvaluation ? "w-1/3" : "w-full")}>
          {isLoading && !evaluations.length ? (
            <View className="flex justify-center items-center h-32">
              <Spinner
                size={32}
                variant="brand"
              />
            </View>
          ) : evaluations.length === 0 ? (
            <EmptyState
              icon="checklist"
              title="No evaluations"
              description="Run sessions or adjust filters to see results."
            />
          ) : (
            <View className="flex-1 border border-gray-200 dark:border-gray-700 rounded-md overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
              {evaluations.map((evaluation) => (
                <EvaluationListItem
                  key={evaluation.id}
                  evaluation={evaluation}
                  evaluator={evaluatorEntities[evaluation.evaluatorId]}
                  agentEnvironmentEntities={agentEnvironmentEntities}
                  environmentEntities={environmentEntities}
                  isActive={selectedEvaluation?.id === evaluation.id}
                  onClick={handleSelectEvaluation}
                />
              ))}
            </View>
          )}

          {totalPages > 1 && (
            <View className="flex flex-row justify-center items-center mt-4 px-2 gap-3">
              <Button
                onClick={() => setPage(Math.max(1, page - 1))}
                isDisabled={page === 1}
                variant="ghost"
                className="flex items-center !p-1.5 rounded-full"
              >
                <Icon
                  name="chevron-left"
                  size={16}
                />
              </Button>
              <Text className="text-sm text-gray-600 dark:text-gray-300">
                Page {page} of {totalPages}
              </Text>
              <Button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                isDisabled={page >= totalPages}
                variant="ghost"
                className="flex items-center !p-1.5 rounded-full"
              >
                <Icon
                  name="chevron-right"
                  size={16}
                />
              </Button>
            </View>
          )}
        </View>

        {selectedEvaluation ? (
          <View className="w-2/3 flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 z-10 overflow-hidden">
            <EvaluationDetailView
              evaluation={selectedEvaluation}
              evaluator={selectedEvaluator}
              agentEnvironmentEntities={agentEnvironmentEntities}
              environmentEntities={environmentEntities}
              agentId={agentId}
              showHeader={true}
              onClose={handleClearSelection}
            />
          </View>
        ) : null}
      </View>
    </View>
  );
};
